import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';

import { fDate, fToNow } from 'src/utils/format-time';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

// Helper function to get icon
const getIcon = (category, customIconMap = {}) => {
  if (!category) return 'solar:bell-bing-bold-duotone';

  const categoryKey = String(category).toLowerCase().trim();

  // Check custom map first
  if (customIconMap[categoryKey]) return customIconMap[categoryKey];

  // Default fallback
  return 'solar:bell-bing-bold-duotone';
};

// Helper function to get color
const getColor = (category, customColorMap = {}) => {
  const defaultColor = { hex: '#637381', variant: 'default' }; // Grey

  if (!category) return defaultColor;

  const categoryKey = String(category).toLowerCase().trim();

  // Check custom map first
  if (customColorMap[categoryKey]) {
    // If custom color is a string, assume it's hex code
    if (typeof customColorMap[categoryKey] === 'string') {
      return { hex: customColorMap[categoryKey], variant: 'default' };
    }
    // If it's an object, return as is
    return customColorMap[categoryKey];
  }

  // Default fallback
  return defaultColor;
};

// Helper function to check date status
const getDateStatus = (dateString) => {
  if (!dateString) return null;

  try {
    const eventDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);

    if (eventDate < today) return 'past';
    if (eventDate.getTime() === today.getTime()) return 'today';
    return 'upcoming';
  } catch (error) {
    console.error('Invalid date:', dateString);
    return null;
  }
};

export function EventItem({
  notification,
  onMarkAsRead,
  // Customization options
  categoryField = 'ScheduleEventCategory.Name', // Path to category field
  titleField = 'Title',
  descriptionField = 'Description',
  dateField = 'Date',
  idField = 'Id',
  isUnReadField = 'isUnRead',
  iconField = 'ScheduleEventCategory.Icon', // Path to icon field in data
  colorField = 'ScheduleEventCategory.ColorCode', // Path to color field in data
  iconMap = {}, // Fallback icon map if data doesn't have icon
  colorMap = {}, // Fallback color map if data doesn't have color
  showDateChip = true,
  showCategoryChip = true,
  showRelativeTime = true,
  avatarIcon = null, // Custom icon override
  avatarColor = null, // Custom color override
}) {
  // Helper to get nested field value
  const getFieldValue = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((acc, part) => acc?.[part], obj) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // Extract values from notification using field paths
  const categoryName = getFieldValue(notification, categoryField, 'Event');
  const title = getFieldValue(notification, titleField, 'Notification');
  const description = getFieldValue(notification, descriptionField, '');
  const date = getFieldValue(notification, dateField, null);
  const notificationId = getFieldValue(notification, idField);
  const isReadValue = getFieldValue(notification, isUnReadField, false);
  // Handle both IsRead (true/false) and isUnRead (true/false) patterns
  const isUnRead = isUnReadField === 'IsRead' ? !isReadValue : isReadValue;

  // Get icon and color from data first, then fallback to maps
  const dataIcon = getFieldValue(notification, iconField, null);
  const dataColor = getFieldValue(notification, colorField, null);

  // Determine final icon
  const categoryIcon = avatarIcon || dataIcon || getIcon(categoryName, iconMap);

  // Determine final color
  let colorData;
  if (avatarColor) {
    // Custom override
    colorData =
      typeof avatarColor === 'string' ? { hex: avatarColor, variant: 'default' } : avatarColor;
  } else if (dataColor) {
    // From API data
    colorData = { hex: dataColor, variant: 'default' };
  } else {
    // From maps or default
    colorData = getColor(categoryName, colorMap);
  }

  const dateStatus = getDateStatus(date);
  const isUrgent = dateStatus === 'today';
  const isPast = dateStatus === 'past';

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: `${colorData.hex}15`, // 15 = 8% opacity in hex
          color: colorData.hex,
        }}
      >
        <Iconify icon={categoryIcon} width={28} />
      </Stack>
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {dateStatus && isUrgent && (
            <Chip
              label="Today"
              size="small"
              color="error"
              variant="filled"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {dateStatus && isPast && (
            <Chip
              label="Expired"
              size="small"
              color="default"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Stack>
      }
      secondary={
        <Box>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 0.5 }}>
              {description}
            </Typography>
          )}
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
            {showDateChip && date && (
              <Chip
                icon={<Iconify icon="solar:calendar-bold-duotone" width={14} />}
                label={fDate(date)}
                size="small"
                variant="soft"
                color={colorData.variant}
                sx={{
                  fontSize: '0.7rem',
                  bgcolor: `${colorData.hex}15`,
                  color: colorData.hex,
                  borderColor: `${colorData.hex}30`,
                }}
              />
            )}
            {showCategoryChip && categoryName && (
              <Chip
                label={categoryName}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.7rem',
                  borderColor: colorData.hex,
                  color: colorData.hex,
                }}
              />
            )}
            {showRelativeTime && date && (
              <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                {fToNow(date)}
              </Typography>
            )}
          </Stack>
        </Box>
      }
    />
  );

  const renderUnReadBadge = isUnRead && (
    <Box
      sx={{
        top: 20,
        width: 10,
        height: 10,
        right: 20,
        borderRadius: '50%',
        bgcolor: isUrgent ? 'error.main' : 'info.main',
        position: 'absolute',
      }}
    />
  );

  return (
    <ListItemButton
      onClick={() => onMarkAsRead && onMarkAsRead(notificationId)}
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.vars.palette.divider}`,
        bgcolor: isUnRead ? 'action.hover' : 'transparent',
        '&:hover': {
          bgcolor: isUnRead ? 'action.selected' : 'action.hover',
        },
      }}
    >
      {renderUnReadBadge}
      {renderAvatar}
      <Stack sx={{ flexGrow: 1 }}>{renderText}</Stack>
    </ListItemButton>
  );
}

EventItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  notification: PropTypes.object.isRequired,
  onMarkAsRead: PropTypes.func,
  categoryField: PropTypes.string,
  titleField: PropTypes.string,
  descriptionField: PropTypes.string,
  dateField: PropTypes.string,
  idField: PropTypes.string,
  isUnReadField: PropTypes.string,
  iconField: PropTypes.string, // Path to icon in notification data
  colorField: PropTypes.string, // Path to color code in notification data
  // eslint-disable-next-line react/forbid-prop-types
  iconMap: PropTypes.object, // Fallback icon mapping
  // eslint-disable-next-line react/forbid-prop-types
  colorMap: PropTypes.object, // Fallback color mapping
  showDateChip: PropTypes.bool,
  showCategoryChip: PropTypes.bool,
  showRelativeTime: PropTypes.bool,
  avatarIcon: PropTypes.string,
  avatarColor: PropTypes.oneOfType([
    PropTypes.string, // Hex code
    PropTypes.shape({
      hex: PropTypes.string,
      variant: PropTypes.string,
    }),
  ]),
};

/* ----------------------------------------------------------------------
 * USAGE EXAMPLES
 * ----------------------------------------------------------------------
 *
 * 1. DEFAULT USAGE (Icon & Color in data):
 *
 *    // Data structure with Icon and ColorCode fields
 *    const notification = {
 *      Id: 1,
 *      Title: "Project Deadline",
 *      Description: "Submit all documents",
 *      Date: "2026-01-01",
 *      isUnRead: true,
 *      ScheduleEventCategory: {
 *        Name: "Project",
 *        Icon: "solar:document-text-bold-duotone",  // Icon in data
 *        ColorCode: "#0288D1"                       // Hex code in data
 *      }
 *    };
 *
 *    <ScheduleNotificationItem
 *      notification={notification}
 *      onMarkAsRead={handleMarkAsRead}
 *    />
 *
 * ----------------------------------------------------------------------
 *
 * 2. CUSTOM DATA STRUCTURE (Icon & Color in different paths):
 *
 *    const customNotification = {
 *      id: 1,
 *      name: "Meeting Reminder",
 *      details: "Team standup at 10 AM",
 *      eventDate: "2024-01-15",
 *      meta: {
 *        type: "meeting",
 *        icon: "solar:users-group-rounded-bold-duotone",
 *        color: "#0288D1"
 *      },
 *      unread: true
 *    };
 *
 *    <ScheduleNotificationItem
 *      notification={customNotification}
 *      titleField="name"
 *      descriptionField="details"
 *      dateField="eventDate"
 *      categoryField="meta.type"
 *      iconField="meta.icon"        // Icon from data
 *      colorField="meta.color"      // Color from data
 *      idField="id"
 *      isUnReadField="unread"
 *    />
 *
 * ----------------------------------------------------------------------
 *
 * 3. FALLBACK ICONS & COLORS (when not in data):
 *
 *    // If Icon or ColorCode is missing from data, use fallback maps
 *    const notification = {
 *      Id: 1,
 *      Title: "Alert",
 *      ScheduleEventCategory: {
 *        Name: "urgent"  // No Icon or ColorCode field
 *      }
 *    };
 *
 *    <ScheduleNotificationItem
 *      notification={notification}
 *      iconMap={{
 *        urgent: 'solar:alarm-bold',
 *        normal: 'solar:bell-bold'
 *      }}
 *      colorMap={{
 *        urgent: '#D32F2F',    // Red hex code (fallback)
 *        normal: '#1976D2'     // Blue hex code (fallback)
 *      }}
 *    />
 *
 *    // Note: Data icon/color takes priority over maps
 *
 * ----------------------------------------------------------------------
 *
 * 4. MINIMAL DISPLAY:
 *
 *    <ScheduleNotificationItem
 *      notification={notification}
 *      showDateChip={false}
 *      showCategoryChip={false}
 *      showRelativeTime={false}
 *    />
 *
 * ----------------------------------------------------------------------
 *
 * 5. OVERRIDE AVATAR (Hex Code):
 *
 *    <ScheduleNotificationItem
 *      notification={notification}
 *      avatarIcon="solar:star-bold"
 *      avatarColor="#ED6C02"  // Orange hex code
 *    />
 *
 *    // Or with variant:
 *    <ScheduleNotificationItem
 *      notification={notification}
 *      avatarIcon="solar:star-bold"
 *      avatarColor={{ hex: '#ED6C02', variant: 'warning' }}
 *    />
 *
 * ----------------------------------------------------------------------
 */
